import clsx from "clsx";
import { AnimatePresence, motion, useAnimate } from "motion/react";
import { cloneElement, FC, ReactElement, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Account, AccountProvider, Network } from "rujira.js";
import { Button } from "../buttons/Button";
import { Input } from "../inputs/Input";
import { Spinner } from "../loader/Loader";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import combined from "../../assets/images/combined.png";
import { useGlobalModalContext } from "../../context/GlobalModal";
import { i18n } from "../../i18n";
import {
  AngleDown,
  Ellipsis,
  Eye,
  EyeSlash,
  LinkConnect,
  LinkDisconnect,
  Plus,
  Users,
} from "../icons/Icons";
import { NetworkIcon } from "../icons/NetworkIcon";
import { Fiat } from "../numbers/Fiat";
import { ResolveLink } from "./ResolveLink";
import { Tooltip } from "react-tooltip";

let referral = localStorage.getItem("referral");

interface AccountsProps<T> {
  provider: AccountProvider<T>;
  routingElement: any;
  wallets: WalletProps<T>[];
  ProviderIcon: FC<{ provider: T; selected: boolean; className?: string }>;
  getValue: (account: Account<T>) => bigint;
  customActions?: ReactElement[];
  isLoading?: boolean;
}

export const Accounts = <T,>({
  provider,
  routingElement,
  wallets,
  ProviderIcon,
  getValue,
  customActions,
}: AccountsProps<T>): ReactElement => {
  const { accounts, disconnectAll, selected } = provider;
  const [showBlur, setShowBlur] = useState(false);
  const { hideModal, showModal } = useGlobalModalContext();
  const [showBalance, setShowBalance] = useLocalStorage<string>(
    "rujira-show-balance",
    "true"
  );

  const ConnectModal = () => {
    showModal({
      title: "Connect Your Account",
      backgroundClose: true,
      children: (
        <Wallets modalLayout={true} provider={provider} wallets={wallets} />
      ),
    });
  };

  const ReferralModal = () => {
    showModal({
      title: "Refer And Earn",
      backgroundClose: true,
      children: (
        <>
          <p className="p balance">
            Share your unique referral link and earn affiliate fees on trades,
            swaps, and all DeFi interactions.
          </p>
          <div
            className="pointer"
            onClick={() => {
              toast.success("referral copied to clipboard");
              navigator.clipboard.writeText(
                `https://rujira.network/?ref=${referral}`
              );
            }}>
            <Input
              value={`https://rujira.network/?ref=${referral}`}
              readOnly={true}
              label="Click to copy"
            />
          </div>
          <p className="fs-12 color-grey mt-2">
            This referral link is valid until ...
          </p>
          <div className="modal__footer mt-4 px-3 py-2 text-right">
            <Button label="Close" onClick={hideModal} />
          </div>
        </>
      ),
    });
  };

  const groupedAccounts = accounts?.reduce(
    (acc, account) => {
      if (account.network === Network.Thorchain) {
        acc.rujira.push(account);
      } else {
        acc.other.push(account);
      }
      return acc;
    },
    { rujira: [], other: [] } as { rujira: Account<T>[]; other: Account<T>[] }
  );
  groupedAccounts?.other.sort((a, b) => a.network.localeCompare(b.network));

  if (selected && groupedAccounts) {
    var selectedIndex = groupedAccounts?.rujira.findIndex(
      (account) =>
        account.network === selected.network &&
        account.provider === selected.provider &&
        account.address === selected.address
    );
    if (selectedIndex > -1 && selectedIndex > 3) {
      // is in hidden list
      const [selectedAccount] = groupedAccounts?.rujira.splice(
        selectedIndex,
        1
      );
      groupedAccounts?.rujira.unshift(selectedAccount);
    } else if (selectedIndex === -1) {
      selectedIndex = groupedAccounts?.other.findIndex(
        (account) =>
          account.network === selected.network &&
          account.provider === selected.provider &&
          account.address === selected.address
      );
      if (selectedIndex > -1 && selectedIndex > 3) {
        // is in hidden list
        const [selectedAccount] = groupedAccounts?.other.splice(
          selectedIndex,
          1
        );
        groupedAccounts?.other.unshift(selectedAccount);
      }
    }
  }

  const [showSelectAccounts, setShowSelectAccounts] = useState(false);
  const ref1 = useClickOutside(() => setShowSelectAccounts(false));
  const [showSelectOther, setShowSelectOther] = useState(false);
  const ref2 = useClickOutside(() => setShowSelectOther(false));

  return (
    <>
      {accounts?.length === 0 ? (
        <ConnectAnimation>
          <Wallets
            provider={provider}
            routingElement={routingElement}
            wallets={wallets}
          />
        </ConnectAnimation>
      ) : (
        <MyAccount
          routingElement={routingElement}
          provider={provider}
          value={selected ? getValue(selected) : false}
          onHide={() => setShowBlur(false)}>
          {selected ? (
            <ResolveLink
              as={routingElement}
              to="/portfolio"
              className="balance">
              <div className="flex ai-b">
                {showBalance === "true" ? (
                  <>
                    <Fiat
                      amount={getValue(selected)}
                      decimals={12}
                      symbol="~$"
                      padSymbol={false}
                      className="color-white fs-26 condensed"
                    />
                    <button
                      className="transparent ml-1 color-grey hover-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowBalance("false");
                        return false;
                      }}
                      style={{ marginRight: "-1rem" }}>
                      <Eye className="w-2 h-2" />
                    </button>
                  </>
                ) : (
                  <>
                    <span
                      className="fiat color-grey fs-22"
                      style={{ fontFamily: "monospace" }}>
                      ****
                    </span>
                    <button
                      className="transparent ml-1 color-grey hover-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowBalance("true");
                        return false;
                      }}
                      style={{ marginRight: "-1rem" }}>
                      <EyeSlash className="w-2 h-2" />
                    </button>
                  </>
                )}
              </div>
              <p className="fs-12 condensed color-grey mt-0.5">
                {i18n.t("View portfolio")}
              </p>
            </ResolveLink>
          ) : null}

          {groupedAccounts?.rujira
            .slice(0, 8)
            .map((account) => (
              <Address
                provider={provider}
                ProviderIcon={ProviderIcon}
                key={`${account.address}${account.network}${account.provider}`}
                account={account}
                selected={
                  selected?.network === account.network &&
                  selected.provider === account.provider &&
                  selected.address === account.address
                }
              />
            ))}
          {groupedAccounts && groupedAccounts.rujira.length > 8 && (
            <div
              className="more flex ai-c px-1"
              onClick={() => {
                setShowSelectAccounts(true);
              }}>
              <Ellipsis className="more__icon" />
              <span className="more__label">
                {groupedAccounts.rujira.length - 8} more accounts
              </span>
              <AngleDown className="mr-0 ml-a w-2 h-2 color-grey" />
              <AnimatePresence>
                {showSelectAccounts && (
                  <motion.div
                    className="more__select"
                    ref={ref1}
                    initial={{ scale: 0.9, translateY: "-50%" }}
                    animate={{ scale: 1, translateY: "-50%" }}
                    exit={{ scale: 0.9, opacity: 0, translateY: "-50%" }}>
                    {groupedAccounts.rujira.slice(8).map((a) => (
                      <Address
                        provider={provider}
                        ProviderIcon={ProviderIcon}
                        key={`${a.address}${a.network}${a.provider}`}
                        account={a}
                        selected={
                          selected?.network === a.network &&
                          selected.provider === a.provider &&
                          selected.address === a.address
                        }
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {showSelectAccounts ||
            (showSelectOther && <div className="stop-interaction" />)}

          <p className="condensed px-1 fs-14 color-grey fw-500 mt-3 mb-1.5">
            Other Accounts
          </p>

          {groupedAccounts?.other
            .slice(0, 8)
            .map((account) => (
              <Address
                provider={provider}
                ProviderIcon={ProviderIcon}
                key={`${account.address}${account.network}${account.provider}`}
                account={account}
                selected={
                  selected?.network === account.network &&
                  selected.provider === account.provider &&
                  selected.address === account.address
                }
              />
            ))}
          {groupedAccounts && groupedAccounts.other.length > 8 && (
            <div
              className="more flex ai-c px-1"
              onClick={() => {
                setShowSelectOther(true);
              }}>
              <Ellipsis className="more__icon" />
              <span className="more__label">
                {groupedAccounts.other.length - 8} more accounts
              </span>
              <AngleDown className="mr-0 ml-a w-2 h-2 color-grey" />
              <AnimatePresence>
                {showSelectOther && (
                  <motion.div
                    className="more__select"
                    ref={ref2}
                    initial={{ scale: 0.9, translateY: "-50%" }}
                    animate={{ scale: 1, translateY: "-50%" }}
                    exit={{ scale: 0.9, opacity: 0, translateY: "-50%" }}>
                    {groupedAccounts?.other
                      .slice(8)
                      .map((a) => (
                        <Address
                          provider={provider}
                          ProviderIcon={ProviderIcon}
                          key={`${a.address}${a.network}${a.provider}`}
                          account={a}
                          selected={
                            selected?.network === a.network &&
                            selected.provider === a.provider &&
                            selected.address === a.address
                          }
                        />
                      ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <nav className="actions">
            {referral && (
              <button className="action transparent" onClick={ReferralModal}>
                <Users />
                <span>Referral Link</span>
              </button>
            )}
            <button className="action transparent" onClick={ConnectModal}>
              <Plus />
              <span>Connect another account</span>
            </button>
            {customActions &&
              customActions.map((e, index) => cloneElement(e, { key: index }))}
            <button className="action transparent" onClick={disconnectAll}>
              <LinkDisconnect />
              <span>Disconnect all</span>
            </button>
          </nav>
          {showBlur && <div className="blur" />}
        </MyAccount>
      )}
    </>
  );
};

const ConnectAnimation = ({ children }: { children: React.ReactNode }) => {
  const [show, setShow] = useState(false);
  const [scope, animate] = useAnimate();

  useEffect(() => {
    if (show) {
      animate(scope.current, { width: 98, color: "#ffffff" });
    } else {
      animate(scope.current, {
        width: scope.current.clientHeight,
        // color: "#ffffff",
      });
    }
  }, [show]);

  return (
    <div
      id="connect-wallet"
      className="relative py-q1"
      onMouseOver={() => setShow(true)}
      onMouseOut={() => setShow(false)}>
      <motion.a
        className="rujira-header__connect"
        ref={scope}
        initial={{ width: 36 }}>
        <AnimatePresence>
          {show && (
            <motion.div
              className="fs-14 fw-500 condensed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}>
              {i18n.t("Connect")}
            </motion.div>
          )}
        </AnimatePresence>
        <LinkConnect />
      </motion.a>
      <AnimatePresence>
        {show && (
          <motion.div
            className="rujira-header__popup right condensed w-32 text-center p-2"
            initial={{ opacity: 0, marginTop: -4 }}
            animate={{ opacity: 1, marginTop: 4 }}
            exit={{ opacity: 0, marginTop: -4 }}>
            <i18n.p className="color-white">Connect Your Account</i18n.p>
            <i18n.small className="color-grey mb-1">
              Create or connect an existing account
            </i18n.small>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export interface WalletProps<T> {
  key: string;
  label: string;
  provider?: T;
  icon: ReactElement;
  url?: string;
}

interface WalletsProps<T> {
  modalLayout?: boolean;
  provider: AccountProvider<T>;
  routingElement?: any;
  wallets: WalletProps<T>[];
}

const Wallets = <T,>({
  modalLayout = false,
  provider,
  routingElement,
  wallets,
}: WalletsProps<T>): ReactElement => {
  const { connect, isAvaialable } = provider;
  const { hideModal } = useGlobalModalContext();

  const [first, ...rest] = wallets;
  return (
    <>
      <div className="rujira-header__wallets">
        <div
          className={clsx({
            "flex w-full ai-c jc-c wrap": true,
            "mb-1": modalLayout,
          })}>
          <button
            id="connect-station"
            data-tooltip-content="Coming Soon..."
            data-tooltip-id="wallet-tip"
            className={clsx({
              "transparent block pointer big": true,
              "desaturate opacity-10": !first?.provider,
            })}
            disabled={!first?.provider}
            onClick={() => {
              first?.provider && connect(first?.provider).then(hideModal);
            }}>
            {first?.icon}
          </button>
        </div>
        <div className="flex w-full ai-c jc-c wrap">
          {rest.map((x) => {
            const disabled = x.provider ? !isAvaialable(x.provider) : true;
            return (
              <button
                key={x.label}
                id={`connect-${x.label}`}
                data-tooltip-content={x.label}
                data-tooltip-id="wallet-tip"
                className={clsx({
                  "transparent block pointer": true,
                  medium: modalLayout,
                  "desaturate opacity-10": disabled,
                })}
                onClick={() => {
                  if (disabled && x.url) {
                    window.open(x.url, "_blank");
                    return;
                  }
                  x.provider && connect(x.provider).then(hideModal);
                }}>
                {x.icon}
              </button>
            );
          })}
        </div>

        <hr
          className={clsx({
            hr: true,
            "my-0.5": !modalLayout,
            "my-2": modalLayout,
          })}
        />
        <ResolveLink as={routingElement} to="/ecosystem/wallets">
          Need help deciding?
        </ResolveLink>
      </div>
      <Tooltip id="wallet-tip" className="tooltip" />
    </>
  );
};

const MyAccount = ({
  provider,
  children,
  onHide,
  routingElement,
  value,
}: {
  provider: AccountProvider<any>;
  children: React.ReactNode;
  onHide: () => void;
  routingElement?: any;
  value: bigint | false;
}) => {
  const [show, setShow] = useState(false);
  const { accounts, selected } = provider;
  const [showBalance] = useLocalStorage<string>("rujira-show-balance", "true");

  useEffect(() => {
    onHide();
  }, [show]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    let toastId: string | null = null;
    if (provider.isLoading) {
      timer = setTimeout(() => {
        if (toastId === null) {
          toastId = toast.loading(
            "We are currently experiencing technical issues that may affect loading times and access to our products.\n\nOur team is working to resolve the issue as quickly as possible."
          );
        }
      }, 5000);
    }
    if (!provider.isLoading) {
      if (toastId !== null) toast.dismiss(toastId);
      return () => {
        if (timer) clearTimeout(timer);
      };
    }
    return () => {
      if (timer) clearTimeout(timer);
      if (toastId !== null) toast.dismiss(toastId);
    };
  }, [provider.isLoading]);

  return (
    <div
      className="relative py-q1"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      <ResolveLink
        as={routingElement}
        to="/portfolio"
        className={`rujira-header__pfp rujira-header__pfp--${selected?.network.toLowerCase()}`}>
        {value !== false && showBalance === "true" && (
          <Fiat amount={value} symbol="~$" decimals={12} padSymbol={false} />
        )}
        {selected && <NetworkIcon network={selected.network} />}
        {accounts?.length && !selected && <img src={combined} alt="combined" />}
        {provider.isLoading && <Spinner />}
      </ResolveLink>
      <AnimatePresence>
        {show && (
          <motion.div
            className="rujira-header__popup right condensed p-1.5 br-2"
            initial={{ opacity: 0, marginTop: -4, right: -8 }}
            animate={{ opacity: 1, marginTop: 4, right: -8 }}
            exit={{ opacity: 0, marginTop: -4, right: -8 }}>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface AddressProps<T> {
  account: Account<T>;
  selected: boolean;
  provider: AccountProvider<T>;
  ProviderIcon: FC<{ provider: T; selected: boolean; className?: string }>;
}

const Address = <T,>({
  account,
  selected,
  provider,
  ProviderIcon,
}: AddressProps<T>): ReactElement => {
  const { select, disconnect } = provider;
  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      className={clsx({
        "flex ai-c px-1 address": true,
        "address--selected": selected,
      })}>
      <NetworkIcon
        network={account.network}
        className="address__wallet"
        selected={selected || hover}
      />
      {/* <ProviderIcon provider={account.provider} selected={selected} /> */}
      <p
        className="address__address pointer"
        onClick={() => {
          if (selected) {
            toast.success("address copied to clipboard");
            navigator.clipboard.writeText(account.address);
          } else {
            select(account);
          }
        }}>
        {hover && selected ? (
          "copy address..."
        ) : (
          <>
            {account.address.substring(0, 8)} ... {account.address.slice(-8)}
          </>
        )}
      </p>

      {hover ? (
        <LinkDisconnect
          className="address__disconnect"
          onClick={() => disconnect(account.provider)}
        />
      ) : (
        <ProviderIcon
          className="address__disconnect"
          provider={account.provider}
          selected={selected}
        />
      )}
    </div>
  );
};
